import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { environment } from '@environments/environments';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { AdminUsersService } from '@office/users/users.service';
import { AdminUserView, SubscriptionView } from '@shared/models/domain/domain-view.model';
import { SelectOption } from '@shared/models/shared/select-option.model';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'formly-autocomplete-users-type',
    standalone: true,
    imports: [MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, FormsModule, ReactiveFormsModule, FormlyModule, MatAutocomplete, MatOption, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <mat-form-field class="w-full">
      <mat-label>{{ props.label }}</mat-label>
      <input
        matInput
        [matAutocomplete]="auto"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [placeholder]="props.placeholder"
        [errorStateMatcher]="errorStateMatcher"
        [required]="props.required"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        [displayWith]="getLabel.bind(this)"
      >
        @if (loading) {
            <mat-option disabled>
                Searching...
            </mat-option>
        }
        @for (item of options$ | async; track item) {
          <mat-option [value]="item?.value">
            {{ item.label }}
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
})
export class AutocompleteUsersTypeComponent extends FieldType<FieldTypeConfig> implements OnInit {
    private destroyRef = inject(DestroyRef);
    private adminUsersService = inject(AdminUsersService);
    private officeTenantsService = inject(OfficeTenantsService);

    options$!: Observable<SelectOption<SubscriptionView[] | SubscriptionView>[]>;
    loading = false;
    ngOnInit(): void {
        this.options$ = this.formControl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            filter(value => typeof value === 'string'),
            filter(value => !(this.props.options as SelectOption[])?.some(option => option.value === value)),
            tap(() => (this.loading = true)),
            switchMap(term => this.searchUsers(term)),
            tap(() => (this.loading = false)),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    private searchUsers(term: string): Observable<SelectOption<SubscriptionView[] | SubscriptionView>[]> {
        if (!term || term.length < 2) {
            return of<SelectOption<SubscriptionView[] | SubscriptionView>[]>([]);
        }
        const websiteName = this.officeTenantsService.getTenantDetailsBySlug()?.name;
        const request = {
            currentPage: 1
            , itemsPerPage: environment.defaultItemsPerPage
            , sort: 'firstName'
            , sortDir: 'asc'
            , 'query': term
            , find: {
                'roleName': 'Customer'
                , ...(websiteName) && { 'websiteNames': websiteName }
                , 'deleted': 'IS NULL'
                , 'subscriptions': 'IS NOT NULL'
            }
        };
        return this.adminUsersService.getUsers(request).pipe(
            catchError(() => of({ items: [] as AdminUserView[] })),
            switchMap(response => {
                const users = response.items || [];
                const options = users.map(user => ({
                    label: `${user.fullName} (${user.email})`,
                    value: user.userId,
                    extra: JSON.parse(user?.subscriptions || '{}') as SubscriptionView[] | SubscriptionView,
                }));
                this.props.options = options;
                return of(options);
            })
        );
    }
    getLabel(value) {
        const options: SelectOption[] = this.props?.options as SelectOption[];
        const filtered = options?.find(v => v.value === value);
        return filtered ? filtered.label : '';
    }
}
